import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  Divider,
  Tab,
  Tabs,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { ServiceLog, ServiceLogFilters, ServiceSchedule } from "./serviceLog";
import { scheduleService, serviceLogService } from "./serviceLogService";
import ServiceLogFiltersComponent from "./components/ServiceLogFilters";
import ServiceLogList from "./components/ServiceLogList";
import ServiceScheduleView from "./components/ServiceScheduleView";
import ServiceLogViewModal from "./components/ServiceLogViewModal";
import { commonButtonProps } from "../../../utils/styles";
import { useToast } from "../../../components/Toast";
import { formatCurrency, formatDate } from "../../../utils/utils";
import NavigationHeader from "../../../components/NavigationHeader";

interface ServiceLogExpense {
  id: string;
  companyId: string;
  serviceLogId: string;
  item: string;
  category: string;
  action: string;
  quantity: number;
  rate: string;
  amount: string;
  gst: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
}

interface ServiceLogResponse {
  id: string;
  companyId: string;
  vehicleId: string;
  serviceDate: string;
  odometer: number;
  garageDetails: {
    id?: string;
    name: string;
    location: string;
    contactName: string;
    contactNumber: string;
  };
  serviceType: string;
  durationSinceLastService: number;
  remarks: string;
  amount: string;
  gst: string;
  totalAmount: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
  expenses: ServiceLogExpense[];
}

const VehicleMaintenance: React.FC = () => {
  const { vehicleId, model } = useParams<{
    vehicleId: string;
    model: string;
  }>();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [serviceLogs, setServiceLogs] = useState<ServiceLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<ServiceLogFilters>({
    serviceDate: new Date().toISOString().split("T")[0],
    serviceType: "",
  });
  const [tabValue, setTabValue] = useState("schedule");
  const [schedule, setSchedule] = useState<ServiceSchedule | null>(null);
  const [noMakeModel, setNoMakeModel] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [logIdToDelete, setLogIdToDelete] = useState<string | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<ServiceLogResponse | null>(
    null
  );

  const fetchServiceLogs = async (filters?: ServiceLogFilters) => {
    try {
      setLoading(true);
      if (!vehicleId) {
        return;
      }
      const response = await serviceLogService.getServiceLogsByVehicle(
        vehicleId,
        filters
      );
      setServiceLogs(response.data);
    } catch (error) {
      showToast("Failed to fetch service logs", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedule = async (model: string) => {
    try {
      setLoading(true);
      if (!vehicleId) {
        return;
      }
      const response = await scheduleService.getScheduleByVehicle(model);
      setSchedule(response.data);
    } catch (error) {
      console.error("Error fetching schedule:", error);
      showToast("Failed to fetch schedule", "error");
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === "logs") {
      fetchServiceLogs(filters);
    } else if (tabValue === "schedule") {
      if (model) {
        fetchSchedule(model);
        setNoMakeModel(false);
      } else {
        setNoMakeModel(true);
        setSchedule(null);
      }
    }
  }, [vehicleId, tabValue, model]);

  const handleFilterChange = async (newFilters: ServiceLogFilters) => {
    setFilters(newFilters);
    fetchServiceLogs(newFilters);
  };

  const handleEditLog = (log: ServiceLog) => {
    navigate(`/vehicles/${vehicleId}/maintenance/${model}/edit/${log.id}`);
  };

  const openDeleteModal = (logId: string) => {
    setLogIdToDelete(logId);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setLogIdToDelete(null);
    setDeleteModalOpen(false);
  };

  const handleDeleteLog = async (logId: string) => {
    try {
      await serviceLogService.deleteServiceLog(logId);
      showToast("Service log deleted successfully", "success");
      fetchServiceLogs();
      closeDeleteModal();
    } catch (error) {
      showToast("Failed to delete service log", "error");
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleViewLog = (log: any) => {
    setSelectedLog(log);
    setViewModalOpen(true);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <NavigationHeader
              breadcrumbs={[
                {
                  label: "Vehicle Details",
                  path: `/vehicles/${vehicleId}`,
                },
                {
                  label: "Vehicle Maintenance",
                },
              ]}
            />
            {tabValue === "logs" && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() =>
                  navigate(`/vehicles/${vehicleId}/maintenance/${model}/add`)
                }
                {...commonButtonProps}
              >
                Add Service Log
              </Button>
            )}
          </Box>
        </Grid>

        {/* Tab Logs | Schedule  */}
        <Grid item xs={12}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Vehicle Maintenance Tabs"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Tab label="Schedule" value="schedule" />
            <Tab label="Service Logs" value="logs" />
          </Tabs>
        </Grid>

        {tabValue === "logs" &&
          (loading ? (
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "center", py: 3 }}
            >
              <CircularProgress />
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <ServiceLogFiltersComponent
                  filters={filters}
                  onFilterChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12}>
                <ServiceLogList
                  serviceLogs={serviceLogs}
                  loading={loading}
                  onEdit={handleEditLog}
                  onDelete={openDeleteModal}
                  onView={handleViewLog}
                />
              </Grid>
            </>
          ))}

        {tabValue === "schedule" && (
          <>
            {noMakeModel ? (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ py: 3, textAlign: "center" }}>
                  Please add model to the vehicle to view schedule
                </Typography>
              </Grid>
            ) : loading ? (
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "center", py: 3 }}
              >
                <CircularProgress />
              </Grid>
            ) : schedule && Object.keys(schedule).length > 0 ? (
              <Grid item xs={12}>
                <ServiceScheduleView
                  schedule={schedule}
                  vehicleModel={model || ""}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ py: 3, textAlign: "center" }}>
                  No schedule available for this vehicle
                </Typography>
              </Grid>
            )}
          </>
        )}
      </Grid>

      {/* View Modal */}
      <ServiceLogViewModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        log={selectedLog}
      />

      {/* Delete Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main", fontWeight: 600 }}>
          Delete Service Log
        </DialogTitle>
        <DialogContent sx={{ pb: 3, pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Are you sure you want to delete this service log? This action cannot
            be undone.
          </Typography>
          <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={closeDeleteModal}
              sx={{ px: 2, py: 0.5, fontSize: "0.8rem" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => logIdToDelete && handleDeleteLog(logIdToDelete)}
              sx={{ px: 2, py: 0.5, fontSize: "0.8rem" }}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default VehicleMaintenance;
